// stores/panelDataStore.load.ts — 1m REST seed orchestration (§4.4, F1 + F1b + Task 2).
//
// Extracted from panelDataStore.ts (to keep it < 250 LOC) following the same
// pattern as panelDataStore.older.ts / .deepen.ts. Owns the candle-gate fetch
// coalescing + the F1b cache fast-path + seedDepth bookkeeping; the store's
// ensureLoaded action is a thin delegate to runEnsureLoaded.
//
// State machine (NEW-3/NEW-4):
//   ready    → no refetch; a LARGER-TF caller re-deepens (maybeDeepen, Task 2).
//   loading  → in-flight seed owns the depth race; coalesce (no-op).
//   cache HIT (refcount cycled 0→1 within session) → seed instantly, NO network,
//             then maybeDeepen in case the cached series is too shallow for this TF.
//   cache MISS → markLoading → loadSymbol1m → setCandles (ready) | markAwaitingSeed
//             (sparse pair, 0 candles) | markError (threw). Caches the result on
//             success so the NEXT re-selection of this symbol is instant.

import {
  loadSymbol1m,
} from '../api/dispatchers/panelSubscription';
import { requiredBaseCandles, maxEnabledMaPeriod } from '../api/candleDepth';
import { getCachedSymbol, setCachedSymbol } from '../api/candleCache';
import { markAwaitingSeed, markError, markLoading, omitKey } from './panelDataMutations';
import { inflight, pendingTeardown } from './panelDataStore.lifecycle';
import { maybeDeepen, backgroundDeepen } from './panelDataStore.deepen';
import type { PanelDataState, SymbolKey } from './panelDataStore.types';

/**
 * FAST first-paint seed size (1m bars). The first load fetches just this many bars so the chart
 * renders almost immediately (a small, fast query) instead of blocking on the full depth-policy
 * fetch (up to SERVER_MAX_LIMIT 10,000 1m rows). The full deep history then loads in the
 * BACKGROUND (backgroundDeepen) for indicator warmup + scroll-back, without blocking the paint.
 * ~720 bars = ~12h of 1m, enough for an immediate, useful chart at any TF's recent window.
 */
const FAST_SEED_BARS = 720;

type Get = () => PanelDataState;
type Set = (
  partial:
    | Partial<PanelDataState>
    | ((s: PanelDataState) => Partial<PanelDataState>),
) => void;

export function runEnsureLoaded(
  get: Get,
  set: Set,
  symbol: SymbolKey,
  interval: number,
): void {
  const s = get();
  // Already settled: don't refetch the seed, but a caller requesting a LARGER
  // TF than the current load's depth (e.g. the same panel raised 1m→4h, which
  // does NOT re-subscribe) gets a one-shot deeper re-fetch (Task 2).
  if (s.dataByKey[symbol]?.loadState === 'ready') {
    maybeDeepen(get, set, symbol, interval);
    return;
  }
  if (s._loading[symbol]) return; // in-flight seed owns the depth race
  // F1b fast path: a within-session re-selection (refcount cycled 0→1) finds the
  // resolved 1m base in the per-symbol cache → seed instantly, NO network. The
  // live ohlc-snapshot/WS still tick on top; a deeper TF still re-deepens.
  const cached = getCachedSymbol(symbol);
  if (cached !== null && cached.candles.length > 0) {
    set((st) => ({ _seedDepth: { ...st._seedDepth, [symbol]: cached.seedDepth } }));
    get().setCandles(symbol, cached.candles, cached.flow); // → 'ready', drains buffer
    maybeDeepen(get, set, symbol, interval); // cached depth too shallow for this TF?
    return;
  }
  set((st) => markLoading(st, symbol)); // → 'loading' (blocks snapshot seed)
  // Two-phase load: the FULL depth-policy size is what this TF ultimately needs, but we paint a
  // SHALLOW fast seed first so the chart appears almost immediately, then deepen in the background.
  const fullDepth = requiredBaseCandles(interval, maxEnabledMaPeriod());
  const fastDepth = Math.min(FAST_SEED_BARS, fullDepth);
  // Record the SHALLOW depth as the current seedDepth so backgroundDeepen knows the gap to fill.
  set((st) => ({ _seedDepth: { ...st._seedDepth, [symbol]: fastDepth } }));
  // onFlow: the slow /api/trade-flow query (6s+ for BTC) resolves INDEPENDENTLY and merges in
  // when it lands — it never blocks the candle paint below.
  const handle = loadSymbol1m(symbol, interval, fastDepth, (flow) => {
    if (get().isSubscribed(symbol) || pendingTeardown.has(symbol)) get().setTradeFlow(symbol, flow);
  });
  inflight.set(symbol, handle);
  const p = handle.promise
    .then(({ candles, flow }) => {
      // Guard: drop the result only if the symbol is GENUINELY gone — no refcount AND
      // no pending (deferred) teardown that a remount could still cancel. During the
      // StrictMode unmount→remount gap the refcount is momentarily 0 with a pending
      // teardown; we must still accept the seed so the remounted panel renders (the
      // empty-chart fix). isSubscribed alone was too strict and dropped valid seeds.
      if (!get().isSubscribed(symbol) && !pendingTeardown.has(symbol)) return;
      if (candles.length === 0) {
        set((st) => markAwaitingSeed(st, symbol, flow)); // sparse pair (NEW-4)
      } else {
        get().setCandles(symbol, candles, flow); // → 'ready', drains buffer → FAST PAINT
        // F1b: cache the resolved 1m base so a within-session re-selection of this
        // symbol skips the network. seedDepth lets a future higher-TF mount decide
        // if the cached series is deep enough (else re-deepen).
        setCachedSymbol(symbol, {
          candles: get().dataByKey[symbol]?.candles ?? candles,
          flow,
          seedDepth: get()._seedDepth[symbol] ?? candles.length,
        });
        // Background deepen: now that the chart is painted, pull the full deep history
        // (indicator warmup + scroll-back) WITHOUT blocking the view. Target the MAX of
        // this load's own depth policy and any depth requested by subscribers that
        // arrived while this seed was in flight (maybeDeepen couldn't act then, and no
        // later subscribe() may ever come — the first-load stuck-shallow race).
        const pending = get()._pendingDeepen[symbol] ?? 0;
        if (pending > 0) {
          set((st) => ({ _pendingDeepen: omitKey(st._pendingDeepen, symbol) }));
        }
        const deepenTarget = Math.max(fullDepth, pending);
        if (deepenTarget > fastDepth) {
          backgroundDeepen(get, set, symbol, deepenTarget);
        }
      }
    })
    .catch(() => {
      if (get().isSubscribed(symbol)) set((st) => markError(st, symbol));
    })
    .finally(() => {
      inflight.delete(symbol);
      set((st) => ({ _loading: omitKey(st._loading, symbol) }));
    });
  set((st) => ({ _loading: { ...st._loading, [symbol]: p } }));
}
